import React, { useMemo } from 'react';
import { Briefcase, Calendar, HelpCircle } from 'lucide-react';

/**
 * Компонент для расчета и отображения рабочих дней
 */
const WorkingDaysCalculator = ({ 
  formData, 
  setFormData, 
  results, 
  isCalculating,
  holidays,
  russianRegions 
}) => {
  
  // Получаем выбранный регион
  const selectedRegion = useMemo(() => {
    return russianRegions.find(region => region.code === formData.regionCode) || { name: 'Россия' };
  }, [formData.regionCode, russianRegions]);
  
  // Фильтруем праздники для выбранного региона
  const filteredHolidays = useMemo(() => {
    if (!formData.includeHolidays) return [];
    
    // Федеральные праздники + региональные праздники для выбранного региона
    return holidays.filter(holiday => 
      holiday.type === 'federal' || 
      holiday.region === formData.regionCode
    );
  }, [holidays, formData.includeHolidays, formData.regionCode]);
  
  // Расчет дат между начальной и конечной датой
  const datesBetween = useMemo(() => {
    if (!formData.startDate || !formData.endDate) return [];
    
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    
    // Проверка корректности дат
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
    
    // Убедимся, что даты в правильном порядке
    const startDate = start <= end ? start : end;
    const endDate = start <= end ? end : start;
    
    // Получаем все даты в диапазоне
    const dates = [];
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  }, [formData.startDate, formData.endDate]);
  
  // Классификация дат на рабочие и выходные
  const classifiedDates = useMemo(() => {
    return datesBetween.map(date => {
      const dayOfWeek = date.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // 0 - воскресенье, 6 - суббота
      
      // Проверка на праздник
      const isHoliday = filteredHolidays.some(holiday => {
        const holidayDate = new Date(holiday.date);
        return holidayDate.getFullYear() === date.getFullYear() &&
               holidayDate.getMonth() === date.getMonth() &&
               holidayDate.getDate() === date.getDate();
      });
      
      // Получаем название праздника, если есть
      const holiday = isHoliday ? filteredHolidays.find(h => {
        const holidayDate = new Date(h.date);
        return holidayDate.getFullYear() === date.getFullYear() &&
               holidayDate.getMonth() === date.getMonth() &&
               holidayDate.getDate() === date.getDate();
      }) : null;
      
      return {
        date,
        isWeekend,
        isHoliday,
        isWorkingDay: !isWeekend && !isHoliday,
        holidayName: holiday?.name
      };
    });
  }, [datesBetween, filteredHolidays]);
  
  // Статистика по дням
  const stats = useMemo(() => {
    const workingDays = classifiedDates.filter(day => day.isWorkingDay).length;
    const weekends = classifiedDates.filter(day => day.isWeekend && !day.isHoliday).length;
    const holidays = classifiedDates.filter(day => day.isHoliday).length;
    
    return {
      total: classifiedDates.length,
      workingDays,
      weekends,
      holidays,
      nonWorkingDays: weekends + holidays
    };
  }, [classifiedDates]);
  
  // Форматирование даты для отображения
  const formatDate = (date) => {
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      weekday: 'long'
    });
  };
  
  if (isCalculating || !formData.startDate || !formData.endDate) {
    return null;
  }
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8 mb-8">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Briefcase className="w-5 h-5 mr-2 text-blue-500" />
        Расчет рабочих дней для {selectedRegion.name}
      </h3>
      
      {/* Информационное сообщение */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-4 border border-blue-100 dark:border-blue-800 flex items-start">
        <HelpCircle className="w-5 h-5 text-blue-500 mr-3 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p className="font-medium mb-1">Как работает расчет рабочих дней</p>
          <p>
            Рабочими считаются дни с понедельника по пятницу, кроме праздничных дней. 
            Для России используется производственный календарь с федеральными праздниками и переносами выходных дней.
            {formData.includeHolidays 
              ? ` Учитываются праздники для региона: ${selectedRegion.name}.` 
              : ' Праздники не учитываются в расчетах.'}
          </p>
        </div>
      </div>
      
      {/* Статистика */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg text-center">
          <h4 className="text-sm text-gray-500 dark:text-gray-400 mb-1">Всего дней</h4>
          <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">{stats.total}</p>
        </div>
        
        <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg text-center">
          <h4 className="text-sm text-green-600 dark:text-green-400 mb-1">Рабочих дней</h4>
          <p className="text-2xl font-bold text-green-700 dark:text-green-300">{stats.workingDays}</p>
        </div>
        
        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg text-center">
          <h4 className="text-sm text-yellow-600 dark:text-yellow-400 mb-1">Выходных</h4>
          <p className="text-2xl font-bold text-yellow-700 dark:text-yellow-300">{stats.weekends}</p>
        </div>
        
        <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg text-center">
          <h4 className="text-sm text-red-600 dark:text-red-400 mb-1">Праздников</h4>
          <p className="text-2xl font-bold text-red-700 dark:text-red-300">{stats.holidays}</p>
        </div>
      </div>
      
      {/* Календарь дат */}
      {classifiedDates.length > 0 && (
        <>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
            <Calendar className="w-4 h-4 mr-1 text-blue-500" />
            Календарь за период
          </h4>
          
          <div className="overflow-auto max-h-64 border border-gray-200 dark:border-gray-700 rounded-lg">
            <table className="min-w-full">
              <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="py-2 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Дата</th>
                  <th className="py-2 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400">День недели</th>
                  <th className="py-2 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Тип дня</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {classifiedDates.map((dayInfo, index) => (
                  <tr 
                    key={index} 
                    className={`
                      ${dayInfo.isHoliday 
                        ? 'bg-red-50 dark:bg-red-900/20' 
                        : dayInfo.isWeekend 
                          ? 'bg-yellow-50 dark:bg-yellow-900/20' 
                          : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }
                    `}
                  >
                    <td className="py-2 px-4 text-sm text-gray-700 dark:text-gray-300">
                      {dayInfo.date.toLocaleDateString('ru-RU')}
                    </td>
                    <td className="py-2 px-4 text-sm text-gray-700 dark:text-gray-300">
                      {dayInfo.date.toLocaleDateString('ru-RU', { weekday: 'long' })}
                    </td>
                    <td className="py-2 px-4 text-sm">
                      {dayInfo.isHoliday ? (
                        <span className="text-red-600 dark:text-red-400 font-medium">
                          Праздник: {dayInfo.holidayName}
                        </span>
                      ) : dayInfo.isWeekend ? (
                        <span className="text-yellow-600 dark:text-yellow-400">
                          Выходной
                        </span>
                      ) : (
                        <span className="text-green-600 dark:text-green-400">
                          Рабочий день
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredHolidays.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Праздничные дни в {selectedRegion.name} ({filteredHolidays.length})
              </h4>
              <div className="text-sm text-gray-600 dark:text-gray-400 grid grid-cols-1 md:grid-cols-2 gap-1">
                {filteredHolidays.slice(0, 10).map((holiday, index) => (
                  <div key={index} className="flex items-center">
                    <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                    <div>
                      {new Date(holiday.date).toLocaleDateString('ru-RU')} - {holiday.name}
                    </div>
                  </div>
                ))}
                {filteredHolidays.length > 10 && (
                  <div className="text-gray-500 dark:text-gray-400 italic">
                    И еще {filteredHolidays.length - 10} праздничных дней...
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WorkingDaysCalculator;